import { useTranslation } from 'next-i18next'
import { useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'

import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFractionSimplifyDivisor from './useFractionSimplifyDivisor'

function FractionSimplifyDivisor({
  answerState,
  actionButtonsFC,
  autofocus = true,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
}: ActivityProps): React.ReactElement {
  const {
    question: { numerator, denominator, divisor },
    answer,
    formValid,
    error,
    onInputChange,
    handleCheckAnswer,
    goToNextQuestion,
  } = useFractionSimplifyDivisor({
    difficultyLevel,
    onAnswerStateChange,
    onNextQuestionClick,
  })
  const { t } = useTranslation(['bootcamp', 'generics'])
  const [inputKey, setInputKey] = useState<string>(uuid())
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEmitInstance(
    onInstanceChange,
    denominator
      ? `\\frac{${numerator} \\div ${divisor}}{${denominator} \\div ${divisor}} = \\frac{\\square}{\\square}`
      : null,
  )

  const handleGoToNextQuestion = (): void => {
    goToNextQuestion()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (formValid) {
      hocOnEnterPress(answerState, handleCheckAnswer, handleGoToNextQuestion)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="flex items-center space-x-1">
          <div>
            <span className="flex items-center space-x-1 justify-center">
              <span className="text-2xl">{numerator}</span>
              <span>{t('bootcamp:signs.division')}</span>
              <span className="text-2xl">{divisor}</span>
            </span>
            <Divider />
            <span className="flex items-center space-x-1 justify-center">
              <span className="text-2xl">{denominator}</span>
              <span>{t('bootcamp:signs.division')}</span>
              <span className="text-2xl">{divisor}</span>
            </span>
          </div>
          <Equals />
          <div className="flex flex-col items-center">
            <Input
              aria-label={t('generics:ariaLabelButtons.insertNumerator')}
              autoFocus={autofocus}
              isError={error && error.numerator}
              key={`${inputKey}-first`}
              maxDigits={3}
              onChange={(value) => onInputChange(true, value)}
              onEnter={onEnterPress}
              ref={inputRef}
              value={`${answer.numerator || ''}`}
            />
            <Divider className="my-2" />
            <Input
              aria-label={t('generics:ariaLabelButtons.insertDenominator')}
              isError={error && error.denominator}
              key={`${inputKey}-second`}
              maxDigits={3}
              onChange={(value) => onInputChange(false, value)}
              onEnter={onEnterPress}
              value={`${answer.denominator || ''}`}
            />
          </div>
        </div>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(),
          goToNextQuestionAction: handleGoToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default FractionSimplifyDivisor
